import { useEffect, useMemo, useState } from 'react';
import { RouteObject } from 'react-router-dom';

import { moduleRegistry } from 'lib/modules';

import { useUserState } from 'providers/user-state-provider';

interface UseModuleRoutesOptions {
  ProtectedRoute: any;
  LazyWrapper: any;
}

/**
 * Hook to generate routes based on user's active vendor modules
 * @param root0
 * @param root0.ProtectedRoute
 * @param root0.LazyWrapper
 */
export function useModuleRoutes({
  ProtectedRoute,
  LazyWrapper,
}: UseModuleRoutesOptions): RouteObject[] {
  const { user } = useUserState();

  // Load active modules when user changes
  useEffect(() => {
    if (!user?.active_vendor?.modules || !user?.role) {
      moduleRegistry.clearActiveModules();
      return;
    }

    moduleRegistry.loadActiveModules(user.active_vendor.modules, user.role);
  }, [user?.active_vendor?.modules, user?.role]);

  // Generate routes for active modules
  const moduleRoutes = useMemo(() => {
    if (!user) {
      return [];
    }

    return moduleRegistry.generateRoutes(ProtectedRoute, LazyWrapper);
  }, [user, ProtectedRoute, LazyWrapper]);

  return moduleRoutes;
}

/**
 * Hook to check if a specific module is active
 * @param moduleId
 */
export function useIsModuleActive(moduleId: string): boolean {
  const { user } = useUserState();
  const [, setTriggerUpdate] = useState({});

  // Load active modules when user changes
  useEffect(() => {
    if (!user?.active_vendor?.modules || !user?.role) {
      moduleRegistry.clearActiveModules();
      return;
    }

    moduleRegistry.loadActiveModules(user.active_vendor.modules, user.role);
    // Trigger a re-render after loading modules
    setTriggerUpdate({});
  }, [user?.active_vendor?.modules, user?.role]);

  // Always check current state of registry
  return moduleRegistry.isModuleActive(moduleId);
}

/**
 * Hook to get all active modules
 */
export function useActiveModules() {
  const { user } = useUserState();
  const [, setTriggerUpdate] = useState({});

  useEffect(() => {
    if (!user?.active_vendor?.modules || !user?.role) {
      moduleRegistry.clearActiveModules();
      return;
    }

    moduleRegistry.loadActiveModules(user.active_vendor.modules, user.role);
    // Trigger a re-render after loading modules
    setTriggerUpdate({});
  }, [user?.active_vendor?.modules, user?.role]);

  return moduleRegistry.getActiveModules();
}
